import { Box, SxProps } from '@mui/material';
import DeployDateTimePicker from '@pages/stands/components/DeployDateTimePicker';
import AccessSelector from '@shared/components/others/AccessSelector/AccessSelector';
import { expiresDateTimeFormat } from '@shared/constants/otherConstants';
import { AccessEnum } from '@shared/types';
import { Dayjs } from 'dayjs';
import { FC, ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTimeValidationError } from '@mui/x-date-pickers';
import TextField from '@mui/material/TextField';

export interface StandOptionsFormPropsNew {
    expiresAt: Dayjs | null;
    access: AccessEnum;
    onExpiresAtChange: (newExpiresAt: Dayjs) => void;
    onAccessChange: (newAccess: AccessEnum) => void;
    comment: string;
    onCommentChange: (newComment: string) => void;
    onError?: (error: DateTimeValidationError) => void;
    extraFields?: ReactNode;
    rootSx?: SxProps;
    hideExpiresAtInput?: boolean;
}

const StandOptionsForm: FC<StandOptionsFormPropsNew> = ({
    expiresAt,
    onExpiresAtChange,
    access,
    onAccessChange,
    comment,
    onCommentChange,
    onError,
    extraFields,
    rootSx,
    hideExpiresAtInput,
}) => {
    const { t } = useTranslation();
    const [standExpireDateError, setStandExpireDateError] = useState<DateTimeValidationError>(null);

    const errorMessage = useMemo(() => {
        switch (standExpireDateError) {
            case 'minDate':
            case 'maxDate': {
                return t('please_select_date');
            }

            case 'disablePast': {
                return t('you_cant_pick_date');
            }

            case 'invalidDate': {
                return t('your_date_not_valid');
            }

            case 'minutesStep': {
                return t('minutes_should_be_rounded_to_hours');
            }

            default: {
                return '';
            }
        }
    }, [standExpireDateError, t]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ...rootSx }}>
            {!hideExpiresAtInput && (
                <DeployDateTimePicker
                    format={expiresDateTimeFormat}
                    label={t('destroy_stand_at')}
                    value={expiresAt}
                    customOnChange={onExpiresAtChange}
                    onError={(error) => {
                        onError?.(error);
                        setStandExpireDateError(error);
                    }}
                    slotProps={{
                        textField: {
                            margin: 'dense',
                            helperText: errorMessage,
                            error: Boolean(errorMessage),
                            size: 'small',
                        },
                    }}
                />
            )}
            <TextField
                value={comment ?? ''}
                onChange={(e) => onCommentChange(e.target.value)}
                label={t('comment')}
                fullWidth
                multiline
                rows={3}
                size="small"
            />
            <AccessSelector data={access} setData={onAccessChange} withHint objectType="stand" />
            {extraFields}
        </Box>
    );
};

export default StandOptionsForm;
